import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import clsx from 'clsx';

export const inputClass =
  'w-full rounded-[3px] border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1 text-[13px] text-[var(--text)] outline-none transition-none placeholder:text-[var(--text-faint)] focus-visible:border-[var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-50';

interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string | null;
  hint?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
  /** Lay the label beside the control instead of above it. */
  inline?: boolean;
}

export function Field({ label, htmlFor, error, hint, required, className, children, inline }: FieldProps) {
  return (
    <div className={clsx(inline ? 'flex items-center gap-2' : 'flex flex-col gap-1', className)}>
      <label
        htmlFor={htmlFor}
        className={clsx(
          'text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]',
          inline && 'w-40 shrink-0 normal-case tracking-normal text-[12.5px] text-[var(--text)]',
        )}
      >
        {label}
        {required && <span className="ml-0.5 text-[var(--danger)]">*</span>}
      </label>
      <div className={inline ? 'flex-1' : undefined}>{children}</div>
      {hint && !error && <p className="text-[11px] text-[var(--text-faint)]">{hint}</p>}
      {error && <p className="text-[11px] text-[var(--danger)]">{error}</p>}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function TextInput(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={clsx(inputClass, className)} {...props} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...props },
  ref,
) {
  return (
    <select ref={ref} className={clsx(inputClass, 'pr-6', className)} {...props}>
      {children}
    </select>
  );
});
